import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { asignarPermisosARol } from "@/features/admin-roles/services/roles-service";
import {
  establecerModuloDeUsuario,
  listarModulosDeUsuario,
  restablecerPermisosDeRol,
  type EstadoModulo,
} from "../services/modulos-service";

/** Módulos de un usuario con su estado (heredado/override/efectivo). */
export const modulosDeUsuarioQueryOptions = (usuarioId: string) =>
  queryOptions({
    queryKey: qk.modulosDeUsuario(usuarioId),
    queryFn: () => listarModulosDeUsuario(usuarioId),
    enabled: usuarioId.length > 0,
  });

export const useModulosDeUsuario = (usuarioId: string) =>
  useQuery(modulosDeUsuarioQueryOptions(usuarioId));

/** Asigna (reemplazo total) los módulos de un rol. */
export function useAsignarModulosARol(opts?: { onGuardado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permisos }: { id: string; permisos: string[] }) => asignarPermisosARol(id, permisos),
    onSuccess: (rol) => {
      toastExito({ title: `Módulos del rol "${rol.nombre}" actualizados` });
      void qc.invalidateQueries({ queryKey: qk.adminRoles });
      opts?.onGuardado?.();
    },
    onError: toastErrorApi,
  });
}

/** Restablece un rol de sistema a sus módulos por defecto. */
export function useRestablecerModulosDeRol(opts?: { onRestablecido?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restablecerPermisosDeRol(id),
    onSuccess: (rol) => {
      toastExito({ title: `Rol "${rol.nombre}" restablecido a sus módulos por defecto` });
      void qc.invalidateQueries({ queryKey: qk.adminRoles });
      opts?.onRestablecido?.();
    },
    onError: toastErrorApi,
  });
}

/** Fija el override de un módulo para un usuario (Heredar/Otorgar/Bloquear). */
export function useEstablecerModuloUsuario(usuarioId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codigo, estado }: { codigo: string; estado: EstadoModulo }) =>
      establecerModuloDeUsuario(usuarioId, codigo, estado),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.modulosDeUsuario(usuarioId) });
    },
    onError: toastErrorApi,
  });
}
